import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import CodeMirror from '@uiw/react-codemirror';
import { json } from '@codemirror/lang-json';

export interface JsonEditorProps {
  value: unknown;
  onChange?: (v: unknown, valid: boolean) => void;
  readOnly?: boolean;
  schema?: object;
  height?: number;
  ariaLabel?: string;
}

export function JsonEditor({
  value,
  onChange,
  readOnly = false,
  height = 320,
  ariaLabel,
}: JsonEditorProps) {
  const { t } = useTranslation();
  const resolvedAriaLabel = ariaLabel ?? t('gateway.shared.json.preview');
  const [text, setText] = useState(() => {
    try {
      return JSON.stringify(value, null, 2);
    } catch {
      return '';
    }
  });
  const [error, setError] = useState<string | null>(null);

  // Sync when value changes externally (read-only mode)
  useEffect(() => {
    if (readOnly) {
      try {
        setText(JSON.stringify(value, null, 2));
      } catch {
        setText('');
      }
    }
  }, [value, readOnly]);

  const handleChange = useCallback(
    (val: string) => {
      setText(val);
      try {
        const parsed = JSON.parse(val);
        setError(null);
        onChange?.(parsed, true);
      } catch (e) {
        const errMsg = e instanceof Error ? e.message : t('gateway.shared.json.invalid');
        setError(errMsg);
        onChange?.(null, false);
      }
    },
    [onChange]
  );

  return (
    <div
      style={{
        border: `1px solid ${error ? 'var(--color-danger)' : 'var(--color-surface-border)'}`,
        borderRadius: 'var(--radius-md)',
        overflow: 'hidden',
        fontFamily: 'var(--font-family-mono)',
      }}
      aria-label={resolvedAriaLabel}
    >
      <CodeMirror
        value={text}
        height={`${height}px`}
        extensions={[json()]}
        onChange={readOnly ? undefined : handleChange}
        readOnly={readOnly}
        theme="light"
        basicSetup={{
          lineNumbers: true,
          foldGutter: true,
          bracketMatching: true,
          autocompletion: !readOnly,
        }}
      />
      {error && (
        <div
          role="alert"
          aria-live="assertive"
          style={{
            padding: 'var(--spacing-2) var(--spacing-3)',
            background: 'var(--color-surface-muted)',
            color: 'var(--color-danger)',
            fontSize: 'var(--font-size-xs)',
            borderTop: '1px solid var(--color-surface-border)',
          }}
        >
          {error}
        </div>
      )}
    </div>
  );
}
